import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, List, ListItem, ListItemText, ListItemAvatar, Avatar, Chip, useMediaQuery, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon, Check as CheckIcon, Close as CloseIcon, 
  AttachMoney as MoneyIcon, AdminPanelSettings as AdminIcon, People as PeopleIcon
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import apiClient from '../../request/http-common';

const WithdrawalApprovalAdmin = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  const { data: pendingWithdrawals, isLoading, error } = useQuery(
    'adminPendingWithdrawals',
    async () => {
      const response = await apiClient.get('/api/admin/joint-withdrawals/pending');
      return response.data;
    },
    {
      refetchInterval: 30000, // Refetch every 30 seconds
      onError: (error) => {
        console.error('Admin Withdrawal Approvals API Error:', error);
      }
    }
  );

  const adminApprovalMutation = useMutation(
    async ({ transactionId, action, adminNotes }) => {
      const response = await apiClient.post(`/api/admin/joint-withdrawals/${transactionId}/approve`, {
        action,
        admin_notes: adminNotes
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('adminPendingWithdrawals');
        setDialogOpen(false);
        setSelectedTransaction(null);
        setAdminNotes('');
      },
      onError: (error) => {
        console.error('Admin approval failed:', error);
        alert('Failed to process admin approval: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  const handleAdminAction = (action) => {
    if (selectedTransaction) {
      adminApprovalMutation.mutate({
        transactionId: selectedTransaction.id,
        action,
        adminNotes
      });
    }
  };

  const openDialog = (transaction) => {
    setSelectedTransaction(transaction);
    setDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Loading pending withdrawals...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load pending withdrawals: {error.message}
        </Alert>
      </Box>
    );
  }

  const transactions = pendingWithdrawals?.pending_withdrawals || {};

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ 
        color: '#FFFFFF', 
        fontWeight: 700, 
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <AdminIcon sx={{ color: '#F7931A' }} />
        Final Approval Dashboard
      </Typography>
      
      <Typography variant="body1" sx={{ color: '#6C7293', mb: 3 }}>
        Review and provide final approval for withdrawal and transfer requests that have received member consensus.
      </Typography>

      {Object.keys(transactions).length === 0 ? (
        <Card sx={{ 
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
          border: '1px solid #2B3139'
        }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <MoneyIcon sx={{ fontSize: 64, color: '#6C7293', mb: 2 }} />
                         <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 1 }}>
              No Transactions Ready for Final Approval
            </Typography>
            <Typography variant="body2" sx={{ color: '#6C7293' }}>
              All withdrawal and transfer requests are either waiting for member consensus or have been processed.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        Object.entries(transactions).map(([jointAccountId, accountTransactions]) => {
          const jointAccount = accountTransactions[0]?.jointAccount;
          
          return (
            <Card key={jointAccountId} sx={{ 
              mb: 3,
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
              border: '1px solid #F7931A'
            }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#F7931A', mb: 2 }}>
                  Joint Account: {jointAccount?.account_name} ({accountTransactions.length} pending)
                </Typography>
                
                <List>
                  {accountTransactions.map((transaction) => {
                    const approvalCount = transaction.approvals?.filter(a => a.action === 'approved').length || 0;
                    const requiredApprovals = jointAccount?.activeMembers?.length || 0;
                    const isReadyForAdmin = approvalCount >= requiredApprovals;
                    
                    return (
                      <ListItem key={transaction.id} sx={{ 
                        border: '1px solid #2B3139', 
                        borderRadius: 1, 
                        mb: 1,
                        background: isReadyForAdmin ? 'rgba(16, 185, 129, 0.1)' : 'rgba(247, 147, 26, 0.1)'
                      }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: isReadyForAdmin ? '#10B981' : '#F7931A' }}>
                            <MoneyIcon />
                          </Avatar>
                        </ListItemAvatar>
                        
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                                ${transaction.amount} - {transaction.initiator?.name}
                              </Typography>
                              <Chip 
                                label={transaction.transaction_type === 'transfer' ? 'Transfer' : 'Withdrawal'} 
                                color="primary" 
                                size="small" 
                              />
                              <Chip 
                                label="Ready for Final Approval" 
                                color="success" 
                                size="small" 
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                                Reference: {transaction.reference}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                                Reason: {transaction.description || 'No reason provided'}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                                Member Approvals: {approvalCount} / {requiredApprovals}
                              </Typography>
                              
                              {/* Enhanced Approval Details */}
                              {transaction.approvals && transaction.approvals.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" sx={{ color: '#6C7293', fontWeight: 600, display: 'block', mb: 0.5 }}>
                                    Member Approval Details:
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {transaction.approvals.map((approval, index) => (
                                      <Chip
                                        key={index}
                                        label={`${approval.approver?.name}: ${approval.action}`}
                                        color={approval.action === 'approved' ? 'success' : 'error'}
                                        size="small"
                                        sx={{ 
                                          fontSize: '0.7rem',
                                          height: '20px',
                                          '& .MuiChip-label': {
                                            px: 1
                                          }
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}
                              
                              {/* Show Approval Progress Bar */}
                              <Box sx={{ mt: 1 }}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  mb: 0.5 
                                }}>
                                  <Typography variant="caption" sx={{ color: '#6C7293' }}>
                                    Approval Progress
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#6C7293' }}>
                                    {approvalCount}/{requiredApprovals}
                                  </Typography>
                                </Box>
                                <Box sx={{ 
                                  width: '100%', 
                                  height: 4, 
                                  bgcolor: '#2B3139', 
                                  borderRadius: 2,
                                  overflow: 'hidden'
                                }}>
                                  <Box sx={{ 
                                    width: `${(approvalCount / requiredApprovals) * 100}%`,
                                    height: '100%',
                                    bgcolor: isReadyForAdmin ? '#10B981' : '#F7931A',
                                    transition: 'width 0.3s ease'
                                  }} />
                                </Box>
                              </Box>
                            </Box>
                          }
                        />
                        
                                                 <Button
                           variant="contained"
                           onClick={() => openDialog(transaction)}
                           sx={{
                             background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                             '&:hover': {
                               background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                             }
                           }}
                         >
                           Review & Approve
                         </Button>
                      </ListItem>
                    );
                  })}
                </List>
              </CardContent>
            </Card>
          );
        })
      )}

      {/* Admin Action Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#FFFFFF', background: '#1a1a1a' }}>
          Final Approval - Withdrawal Request
        </DialogTitle>
        <DialogContent sx={{ background: '#1a1a1a', color: '#FFFFFF' }}>
          {selectedTransaction && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Amount: ${selectedTransaction.amount}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Requested by: {selectedTransaction.initiator?.name}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Reason: {selectedTransaction.description || 'No reason provided'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Reference: {selectedTransaction.reference}
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Admin Notes (Optional)"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': {
                      borderColor: '#6C7293',
                    },
                    '&:hover fieldset': {
                      borderColor: '#F7931A',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#6C7293',
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ background: '#1a1a1a', p: 2, gap: 1 }}>
          <Button 
            onClick={() => setDialogOpen(false)}
            sx={{ color: '#6C7293' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => handleAdminAction('rejected')}
            variant="outlined"
            color="error"
            disabled={adminApprovalMutation.isLoading}
            sx={{
              borderColor: '#F84960',
              color: '#F84960',
              '&:hover': {
                borderColor: '#DC2626',
                backgroundColor: 'rgba(248, 73, 96, 0.1)'
              }
            }}
          >
            Reject
          </Button>
          <Button 
            onClick={() => handleAdminAction('approved')}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
              }
            }}
            disabled={adminApprovalMutation.isLoading}
          >
            Final Approval
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WithdrawalApprovalAdmin; 